import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useData } from "../contexts/DataContext";
import ReactMarkdown from "react-markdown";
import { useAuth } from "../contexts/AuthContext";
import Button from "../components/Buttons/Button";
import BlurHashAvatar from "../components/BlurHashAvatar";
import { getFirestore, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { useFirebase } from "../contexts/FirebaseContext";
import { Helmet } from 'react-helmet-async';
import Pill from "../components/Pill";

export default function Blog() {
  const { currentUser } = useAuth();
  const { slug } = useParams();
  const { blogsData, loading, error } = useData();
  const { app } = useFirebase();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();

  // Helper function to check if user can view a blog
  const userCanView = (blog: any) => {
    if (!blog) return false;
    const isAdmin = currentUser?.admin;
    const isAuthor = currentUser?.uid === blog.author_id;
    
    // Anyone can view approved, non-deleted posts
    if (blog.approved && !blog.deleted) return true;
    if (blog.deleted) return false;
    if (!blog.approved && (!isAdmin && !isAuthor)) return false;
    
    // Only admins and authors can view deleted or unapproved posts
    if (isAdmin || isAuthor) {
        return true;
    } 
  };

  const blog = slug ? blogsData?.find((blog) => blog.slug === slug) : null;

  // Redirect unauthorized users trying to access restricted posts
  useEffect(() => {
    if (blog && slug && !userCanView(blog)) {
      navigate('/blog', { 
        replace: true,
        state: { 
          error: blog.deleted 
            ? 'This blog post has been deleted.' 
            : 'This blog post is pending approval.'
        }
      });
    }
  }, [blog, slug, currentUser, navigate]);

  // Add state error handling at the top level
  const [locationState, setLocationState] = useState<any>(null);
  
  useEffect(() => {
    const state = window.history.state?.usr;
    if (state?.error) {
      setLocationState(state);
      // Clear the error after displaying it
      window.history.replaceState(
        { ...window.history.state, usr: { ...state, error: null } },
        ''
      );
    }
  }, []);

  const userCanEdit = (authorId: string) => {
    if (!currentUser) return false;
    return currentUser.admin || currentUser.uid === authorId;
  };

  const handleDelete = async (blogId: string | undefined) => {
    if (!blogId) return;
    try {
      const db = getFirestore(app);
      await updateDoc(doc(db, 'blogs', blogId), {
        deleted: true,
        deleted_at: new Date().toISOString()
      });
      setShowDeleteModal(false);
      navigate('/blog');
    } catch (error) {
      console.error('Error marking blog as deleted:', error);
    }
  };

  // Helper function to check if a blog is live (approved and not deleted)
  const isLiveBlog = (blog: any) => {
    return blog.approved && !blog.deleted;
  };

  // Helper function to order live blogs first
  const orderLiveFirst = (blogs: any[]) => {
    return [...blogs].sort((a, b) => {
      if (isLiveBlog(a) && !isLiveBlog(b)) return -1;
      if (!isLiveBlog(a) && isLiveBlog(b)) return 1;
      // For blogs with same status, sort by creation date (newest first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  if (slug) {
    if (!blog || !userCanView(blog)) return <div>Blog post not found</div>;

    const preview = blog.content
      .replace(/[#*`_~]/g, '')
      .substring(0, 140)
      .trim() + '...';

    return (
      <>
        <Helmet>
          <title>{blog.title} | Soundness Events</title>
          <meta name="description" content={preview} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={window.location.href} />
          <meta property="og:title" content={`${blog.title} | Soundness Events`} />
          <meta property="og:description" content={preview} />
          <meta property="og:site_name" content="Soundness Events" />
          <meta name="author" content={blog.author} />
          {blog.header_image && (
            <meta property="og:image" content={blog.header_image} />
          )}
        </Helmet>
        <div className="container mx-auto px-4 py-8">
          {blog.header_image && (
            <div className="mb-8 w-full h-64 relative">
              <BlurHashAvatar
                src={blog.header_image}
                blurHash={blog.header_image_blur_hash}
                alt={blog.title}
                className="w-full h-full"
                containerClassName="w-full h-full"
                roundedClassName="rounded-lg"
              />
            </div>
          )}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">{blog.title}</h1>
              <div className="flex gap-2">
                {blog.deleted && (
                  <Pill
                    text="Deleted"
                    colorClass="bg-muted-sage"
                    size="small"
                  />
                )}
                {!blog.approved && (
                  <Pill
                    text="Pending Approval"
                    colorClass="bg-muted-sage"
                    size="small"
                  />
                )}
              </div>
            </div>
            {userCanEdit(blog.author_id) && (
              <div className="flex gap-2">
                <Link to={`/blog/submit?edit=${blog.id}`}>
                  <Button color="moss text-black">
                    Edit
                  </Button>
                </Link>
                <Button
                  color="clay"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete
                </Button>
              </div>
            )}
          </div>
          <div className="mb-4">
            <span className="text-gray-600">By {blog.author}</span>
            <span className="text-gray-600 ml-4">
              {new Date(blog.created_at).toLocaleDateString()}
            </span>
          </div>
          <div className="prose max-w-prose markdown">
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => (
                  <a
                    {...props}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  />
                ),
              }}
            >
              {blog.content}
            </ReactMarkdown>
          </div>

          {/* Delete Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg p-6 max-w-sm w-full">
                <h3 className="text-xl font-semibold mb-4">Delete Confirmation</h3>
                <p className="mb-6">Are you sure you would like to delete "{blog.title}"?</p>
                <div className="flex justify-end gap-4">
                  <Button
                    color="moss text-black"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    No
                  </Button>
                  <Button
                    color="clay"
                    onClick={() => handleDelete(blog.id)}
                  >
                    Yes
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  // Otherwise, show the list of blog posts
  return (
    <>
      <Helmet>
        <title>Blog Posts | Soundness Events</title>
        <meta name="description" content="Explore our collection of articles about movement, wellness, and healing practices." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content="Blog Posts | Soundness Events" />
        <meta property="og:description" content="Explore our collection of articles about movement, wellness, and healing practices." />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        {/* Show error message if redirected */}
        {locationState?.error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {locationState.error}
          </div>
        )}

        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Blog Posts</h1>
          {currentUser && (
            <Button
              onClick={() => (window.location.href = "/blog/submit")}
              className="ml-4"
            >
              Submit a Blog
            </Button>
          )}
        </div>
        <div className="grid gap-6">
          {orderLiveFirst(blogsData)
            .filter(blog => userCanView(blog))
            .map((blog) => (
              <div key={blog.id} className="relative">
                <Link
                  to={`/blog/${blog.slug}`}
                  className="block p-6 border rounded-lg hover:shadow-lg transition-shadow"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h2 className="text-xl font-semibold">{blog.title}</h2>
                    <div className="flex gap-2">
                      {blog.deleted && (
                        <Pill
                          text="Deleted"
                          colorClass="bg-muted-clay"
                          size="small"
                        />
                      )}
                      {!blog.approved && (
                        <Pill
                          text="Pending Approval"
                          colorClass="bg-muted-sage"
                          size="small"
                        />
                      )}
                    </div>
                  </div>
                  <div className="text-gray-600 mb-2">
                    By {blog.author} • {new Date(blog.created_at).toLocaleDateString()}
                  </div>
                  <p className="text-gray-700 prose max-w-prose">
                    {blog.content.substring(0, 200)}...
                  </p>
                </Link>
              </div>
            ))}
        </div>
      </div>
    </>
  );
}
